//////////////////
// IMPORTS
//////////////////
// libraries
import * as React from "react";
// calendar-color-picker imports
import {
  IProps,
  IMenuProps,
  IMenuState,
  IColorOptionProps,
  INullOptionProps,
} from "./CalendarColorPicker.d";
import * as s from "./CalendarColorPicker.styles";
// util
import { calendarColors, getColorFromSetting } from "@util/event-category-coloring.util";
// material-ui
import { IconButton, Menu, Tooltip } from "@material-ui/core";
import { Block, Brightness1, CheckCircle, ArrowDropDown } from "@material-ui/icons";

/////////////////////
// COMMON COMPONENTS
/////////////////////
const NullColorOption = (props: INullOptionProps) => {
  const { active, onSelect, tooltipPosition, selectedColorIndex } = props;
  const defaultIsActive = !active || !selectedColorIndex;
  return (
    <Tooltip title="Default" placement={tooltipPosition || "top-end"}>
      <IconButton style={s.smallBtn} onClick={() => onSelect(null)}>
        <Block style={{ ...s.smallIcon, color: defaultIsActive ? "red" : "grey" }} />
      </IconButton>
    </Tooltip>
  );
};

const ColorOption = (props: IColorOptionProps) => {
  const { selectedColorIndex, active, onSelect, color, colorKey, tooltipPosition } = props;
  const colorIsActive = active && colorKey === selectedColorIndex;

  return (
    <Tooltip title={color.name} placement={tooltipPosition || "top-end"}>
      <IconButton key={color.background} onClick={() => onSelect(colorKey)} style={s.smallBtn}>
        {colorIsActive ? (
          <CheckCircle style={{ ...s.smallIcon, color: color.foreground }} />
        ) : (
          <Brightness1 style={{ ...s.smallIcon, color: color.foreground }} />
        )}
      </IconButton>
    </Tooltip>
  );
};

/////////////////////
// MENU COMPONENT
/////////////////////
export class CalendarColorPickerMenu extends React.Component<IMenuProps, IMenuState> {
  constructor(props: IMenuProps) {
    super(props);
    this.state = {
      open: false,
      hovered: false,
      anchorEl: null,
    };
  }
  private onHover = (hovered: boolean) => {
    this.setState({ hovered });
  };

  private onClickToOpen = (event: React.MouseEvent<HTMLElement>) => {
    // This prevents ghost click.
    event.preventDefault();
    this.setState({
      open: true,
      anchorEl: event.currentTarget,
    });
  };

  private onRequestClose = () => {
    this.setState({ open: false });
  };

  private onSelect = (selectedColorIndex: string | null) => {
    this.onRequestClose();
    this.props.onSelect(selectedColorIndex);
  };

  private getTooltipPosition = (index: number): IColorOptionProps["tooltipPosition"] => {
    switch (index) {
      case 1:
        return "bottom-start";
      case 2:
      case 3:
        return "bottom";
      case 4:
        return "bottom-end";
      case 5:
      case 9:
        return "top-start";
      case 6:
      case 7:
      case 10:
      case 11:
        return "top";
      case 8:
      case 12:
      default:
        return "top-end";
    }
  };

  public render() {
    const { open, anchorEl, hovered } = this.state;
    const color = getColorFromSetting(this.props.eventCategoryColoring);
    const colorIsActive = !!color && this.props.eventCategoryColoring.active;

    return (
      <div style={s.container}>
        <IconButton
          onClick={this.onClickToOpen}
          onMouseEnter={() => this.onHover(true)}
          onMouseLeave={() => this.onHover(false)}
        >
          <ArrowDropDown style={hovered ? s.arrowDownHover : s.arrowDown} />
          {!!color && colorIsActive ? <CheckCircle htmlColor={color} /> : <Block />}
        </IconButton>
        <Menu
          open={open}
          anchorEl={anchorEl}
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          onClose={this.onRequestClose}
          onBackdropClick={this.onRequestClose}
          onEscapeKeyDown={this.onRequestClose}
        >
          <div style={s.popoverColors}>
            <NullColorOption
              {...this.props}
              tooltipPosition="bottom-end"
              onSelect={this.onSelect}
            />
            {Object.keys(calendarColors).map((key, i) => {
              const calColor = calendarColors[key];
              const tooltipPosition = this.getTooltipPosition(i + 1);
              return (
                <ColorOption
                  {...this.props}
                  key={key}
                  tooltipPosition={tooltipPosition}
                  color={calColor}
                  colorKey={key}
                  onSelect={this.onSelect}
                />
              );
            })}
          </div>
        </Menu>
      </div>
    );
  }
}

/////////////////////
// LIST COMPONENT
/////////////////////
export class CalendarColorPicker extends React.Component<IProps> {
  constructor(props: IProps) {
    super(props);
  }

  public render() {
    return (
      <div style={s.container}>
        <NullColorOption {...this.props} />
        {Object.keys(calendarColors).map((key) => {
          const color = calendarColors[key];
          return <ColorOption key={key} color={color} colorKey={key} {...this.props} />;
        })}
      </div>
    );
  }
}
